import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ICrmError, ICrmTask, ICrmTaskInput, ICrmTaskPatch } from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type CrmTasksKeyFunctions = {
    /** whose: 'mine' (default) = tasks I owe; 'given' = tasks I handed to teammates. */
    list: (view?: 'open' | 'done' | 'all', leadId?: number, whose?: 'mine' | 'given') => Promise<ICrmTask[] | ICrmError>;
    /** Every open task on a lead, whoever owns it. */
    listForLead: (leadId: number) => Promise<ICrmTask[] | ICrmError>;
    countDue: () => Promise<number | ICrmError>;
    create: (input: ICrmTaskInput) => Promise<ICrmTask | ICrmError>;
    update: (id: number, patch: ICrmTaskPatch) => Promise<ICrmTask | ICrmError>;
    remove: (id: number) => Promise<true | ICrmError>;
};

/** A rep's tasks: to-dos, follow-ups, and email snoozes in one list. */
export const useCrmTasks = (): [CrmTasksKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return {
            ok: false,
            status,
            message: err?.response?.data?.message
                || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        };
    };

    return [{
        list: async (view = 'open', leadId, whose) => {
            try {
                const r: AxiosResponse<ICrmTask[]> = await axios.get(`${crmUrl}/tasks`, { headers, params: { view, lead: leadId, whose } });
                return r.data;
            } catch (err: any) { return handle(err); }
        },
        listForLead: async (leadId) => {
            try { const r: AxiosResponse<ICrmTask[]> = await axios.get(`${crmUrl}/leads/${leadId}/tasks`, { headers }); return r.data; } catch (err: any) { return handle(err); }
        },
        countDue: async () => {
            try { const r: AxiosResponse<{ due: number }> = await axios.get(`${crmUrl}/tasks/count`, { headers }); return r.data.due; } catch (err: any) { return handle(err); }
        },
        create: async (input) => {
            try { const r: AxiosResponse<ICrmTask> = await axios.post(`${crmUrl}/tasks`, input, { headers }); return r.data; } catch (err: any) { return handle(err); }
        },
        update: async (id, patch) => {
            try { const r: AxiosResponse<ICrmTask> = await axios.put(`${crmUrl}/tasks/${id}`, patch, { headers }); return r.data; } catch (err: any) { return handle(err); }
        },
        remove: async (id) => {
            try { await axios.delete(`${crmUrl}/tasks/${id}`, { headers }); return true; } catch (err: any) { return handle(err); }
        }
    }];
};
